import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { getPublicProfile } from '../services/users.service';
import { apiFetch } from '../services/api';
import { FaUser, FaStar, FaBox, FaCar, FaHandHolding, FaArrowLeft, FaCheckCircle, FaShieldAlt } from 'react-icons/fa';
import ReportModal from '../components/ReportModal';

const ROL_LABELS = {
  DRIVER:   { label: 'Viajero',        icon: <FaCar />,         color: '#3b82f6', bg: '#3b82f615' },
  SENDER:   { label: 'Solicitó ayuda', icon: <FaBox />,         color: '#f97316', bg: '#f9731615' },
  RECEIVER: { label: 'Receptor',       icon: <FaHandHolding />, color: '#22c55e', bg: '#22c55e15' },
};

function StarRow({ score, size = 13 }) {
  return (
    <span className="flex gap-0.5">
      {[1,2,3,4,5].map(i => (
        <FaStar key={i} style={{color: i <= score ? '#f59e0b' : '#374151', fontSize: size}} />
      ))}
    </span>
  );
}

function RatingCard({ rating, onViewProfile }) {
  const rol = ROL_LABELS[rating.role];
  return (
    <div style={{backgroundColor:'#1a1d24', border:'1px solid #2a2d35', borderRadius:'16px', padding:'16px', marginBottom:'10px'}}>
      <div style={{display:'flex', alignItems:'center', gap:'12px', marginBottom:'10px'}}>
        <div onClick={() => onViewProfile(rating.fromUser.id)} style={{cursor:'pointer', flexShrink:0}}>
          {rating.fromUser.avatar ? (
            <img src={rating.fromUser.avatar}
              style={{width:'38px', height:'38px', borderRadius:'50%', objectFit:'cover'}} />
          ) : (
            <div style={{width:'38px', height:'38px', borderRadius:'50%', backgroundColor:'#f97316',
              display:'flex', alignItems:'center', justifyContent:'center', fontWeight:'bold', color:'white', fontSize:'14px'}}>
              {rating.fromUser.name?.charAt(0) || '?'}
            </div>
          )}
        </div>
        <div style={{flex:1, cursor:'pointer'}} onClick={() => onViewProfile(rating.fromUser.id)}>
          <p style={{color:'white', fontSize:'13px', fontWeight:'600'}}>{rating.fromUser.name}</p>
          <p style={{color:'#6b7280', fontSize:'11px'}}>{new Date(rating.createdAt).toLocaleDateString('es-ES')}</p>
        </div>
        <span style={{backgroundColor: rol.bg, color: rol.color,
          fontSize:'10px', fontWeight:'600', padding:'3px 10px', borderRadius:'20px',
          display:'flex', alignItems:'center', gap:'4px'}}>
          {rol.icon} {rol.label}
        </span>
      </div>
      <StarRow score={rating.score} />
      {rating.comment && (
        <p style={{color:'#d1d5db', fontSize:'13px', marginTop:'10px', lineHeight:'1.6', fontStyle:'italic',
          borderTop:'1px solid #2a2d35', paddingTop:'10px'}}>
          "{rating.comment}"
        </p>
      )}
    </div>
  );
}

export default function PublicProfile({ userId, onBack }) {
  const { token, user: me } = useAuth();
  const [profile, setProfile] = useState(null);
  const [message, setMessage] = useState('');
  const [filtroRol, setFiltroRol] = useState('TODOS');
  const [viewingProfile, setViewingProfile] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [interacciones, setInteracciones] = useState([]);
  const [loadingInteracciones, setLoadingInteracciones] = useState(false);
  const [interaccionSeleccionada, setInteraccionSeleccionada] = useState(null);
  const [formData, setFormData] = useState({ score: 5, comment: '' });
  const [sending, setSending] = useState(false);
  const [formMsg, setFormMsg] = useState('');
  const [showReport, setShowReport] = useState(false);
  const [showFoto, setShowFoto] = useState(false);

  const cargar = () =>
    getPublicProfile(userId, token)
      .then(data => setProfile(data))
      .catch(() => setMessage('Error cargando perfil'));

  useEffect(() => { cargar(); }, [userId, token]);

  const abrirFormulario = async () => {
    if (showForm) { setShowForm(false); return; }
    setShowForm(true);
    setLoadingInteracciones(true);
    try {
      const data = await apiFetch(`/users/${userId}/interacciones`, {}, token);
      setInteracciones(data);
    } catch { setInteracciones([]); }
    finally { setLoadingInteracciones(false); }
  };

  const handleSubmit = async () => {
    if (!interaccionSeleccionada) { setFormMsg('Selecciona una interacción'); return; }
    setSending(true); setFormMsg('');
    try {
      await apiFetch(`/users/${userId}/ratings`, {
        method: 'POST',
        body: JSON.stringify({
          packageId: interaccionSeleccionada.packageId,
          role: interaccionSeleccionada.rolTarget,
          score: parseInt(formData.score),
          comment: formData.comment || undefined
        })
      }, token);
      setFormMsg('¡Agradecimiento enviado!');
      setShowForm(false);
      setInteraccionSeleccionada(null);
      cargar();
    } catch (err) {
      setFormMsg(err.message || 'Error al enviar');
    } finally { setSending(false); }
  };

  const ratingsFiltradas = profile?.ratings?.filter(r =>
    filtroRol === 'TODOS' ? true : r.role === filtroRol
  ) || [];

  if (viewingProfile) return (
    <PublicProfile userId={viewingProfile} onBack={() => setViewingProfile(null)} />
  );

  if (!profile) return (
    <div style={{minHeight:'100vh', backgroundColor:'#111318', display:'flex', alignItems:'center', justifyContent:'center'}}>
      <p style={{color:'#6b7280', fontSize:'14px'}}>Cargando...</p>
    </div>
  );

  const interaccionesDisponibles = interacciones.filter(i => !i.yaValorado);

  return (
    <div style={{minHeight:'100vh', backgroundColor:'#111318', paddingBottom:'96px'}}>

      {/* CABECERA */}
      <div style={{position:'relative', marginBottom:'60px'}}>
        {/* Fondo sutil */}
        <div style={{height:'180px', borderBottom:'1px solid #2a2d35', overflow:'hidden', position:'relative'}}>
  {profile.avatar && (
    <img src={profile.avatar} alt=""
      style={{position:'absolute', top:0, left:0, width:'100%', height:'100%',
        objectFit:'cover', filter:'blur(20px) brightness(0.4) saturate(1.5)',
        transform:'scale(1.1)'}} />
  )}
  <div style={{position:'absolute', inset:0, background:'linear-gradient(180deg, transparent 0%, #111318 100%)'}} />
</div>

        {/* Botón volver */}
        <button onClick={onBack}
          style={{position:'absolute', top:'16px', left:'16px', backgroundColor:'#2a2d35',
            color:'#9ca3af', padding:'8px 14px', borderRadius:'20px', border:'none',
            display:'flex', alignItems:'center', gap:'6px', fontSize:'13px', cursor:'pointer'}}>
          <FaArrowLeft style={{fontSize:'11px'}} /> Volver
        </button>

        {/* Avatar centrado */}
        <div style={{position:'absolute', bottom:'-50px', left:0, right:0, display:'flex', justifyContent:'center'}}>
          <div style={{position:'relative'}}>
            {profile.avatar ? (
              <img src={profile.avatar}
                onClick={() => setShowFoto(true)}
                style={{width:'96px', height:'96px', borderRadius:'50%', objectFit:'cover',
                  border:'4px solid #111318', boxShadow:'0 8px 32px rgba(0,0,0,0.6)', cursor:'pointer'}} />
            ) : (
              <div style={{width:'96px', height:'96px', borderRadius:'50%', backgroundColor:'#f97316',
                border:'4px solid #111318', display:'flex', alignItems:'center', justifyContent:'center',
                boxShadow:'0 8px 32px rgba(0,0,0,0.6)'}}>
                <FaUser style={{color:'white', fontSize:'32px'}} />
              </div>
            )}
            {/* Badge de valoración sobre avatar */}
            {profile.stats.global && (
              <div style={{position:'absolute', bottom:'-4px', right:'-4px',
                backgroundColor:'#f59e0b', borderRadius:'20px', padding:'2px 8px',
                border:'2px solid #111318', display:'flex', alignItems:'center', gap:'3px'}}>
                <FaStar style={{color:'white', fontSize:'9px'}} />
                <span style={{color:'white', fontSize:'11px', fontWeight:'800'}}>{profile.stats.global}</span>
              </div>
            )}
          </div>
        </div>
      </div>

      <div style={{maxWidth:'480px', margin:'0 auto', padding:'0 16px'}}>
        {message && <p style={{color:'#f87171', fontSize:'13px', textAlign:'center', marginBottom:'16px'}}>{message}</p>}

      {/* NOMBRE Y FECHA */}
        <div style={{textAlign:'center', marginBottom:'16px'}}>
          <h2 style={{color:'white', fontWeight:'800', fontSize:'22px', marginBottom:'4px'}}>{profile.name}</h2>
          {profile.perfilVerificado === 'verificado' && (
  <span style={{backgroundColor:'#22c55e15', border:'1px solid #22c55e30',
    color:'#22c55e', fontSize:'11px', fontWeight:'600', padding:'3px 10px',
    borderRadius:'20px', display:'inline-block', marginTop:'4px'}}>
    ✓ Verificado por la comunidad
  </span>
)}
{profile.perfilVerificado === 'revision' && (
  <span style={{backgroundColor:'#f8717115', border:'1px solid #f8717130',
    color:'#f87171', fontSize:'11px', fontWeight:'600', padding:'3px 10px',
    borderRadius:'20px', display:'inline-block', marginTop:'4px'}}>
    ⚠️ Perfil en revisión
  </span>
)}
{(!profile.perfilVerificado || profile.perfilVerificado === 'pendiente') && (
  <span style={{backgroundColor:'#f59e0b15', border:'1px solid #f59e0b30',
    color:'#f59e0b', fontSize:'11px', fontWeight:'600', padding:'3px 10px',
    borderRadius:'20px', display:'inline-block', marginTop:'4px'}}>
    🟡 Pendiente de verificación
  </span>
)}
          <p style={{color:'#6b7280', fontSize:'12px'}}>
            En la comunidad desde {new Date(profile.createdAt).toLocaleDateString('es-ES', {month:'long', year:'numeric'})}
          </p> 
          {profile.stats.total > 0 && (
            <div style={{display:'flex', alignItems:'center', justifyContent:'center', gap:'6px', marginTop:'8px'}}>
              <StarRow score={Math.round(profile.stats.global)} size={14} />
              <span style={{color:'white', fontWeight:'700', fontSize:'14px'}}>{profile.stats.global}</span>
              <span style={{color:'#6b7280', fontSize:'12px'}}>· {profile.stats.total} agradecimientos</span>
            </div>
          )}
          {!profile.stats.total && (
            <p style={{color:'#6b7280', fontSize:'12px', marginTop:'8px'}}>Nuevo en la comunidad 🌱</p>
          )}
        </div>

        {/* BADGES */}
        <div style={{display:'flex', flexWrap:'wrap', justifyContent:'center', gap:'6px', marginBottom:'20px'}}>
          {profile.totalEnvios >= 1 && (
            <span style={{backgroundColor:'#f9731615', border:'1px solid #f9731630',
              color:'#f97316', fontSize:'11px', fontWeight:'600', padding:'4px 12px', borderRadius:'20px'}}>
              🤝 {profile.totalEnvios} ayudas
            </span>
          )}
          {profile.stats.porRol.DRIVER && (
            <span style={{backgroundColor:'#3b82f615', border:'1px solid #3b82f630',
              color:'#3b82f6', fontSize:'11px', fontWeight:'600', padding:'4px 12px', borderRadius:'20px'}}>
              🚗 Viajero activo
            </span>
          )}
          {profile.stats.global >= 4.5 && profile.stats.total >= 3 && (
            <span style={{backgroundColor:'#f59e0b15', border:'1px solid #f59e0b30',
              color:'#f59e0b', fontSize:'11px', fontWeight:'600', padding:'4px 12px', borderRadius:'20px'}}>
              ⭐ Muy valorado
            </span>
          )}
        </div>

        {/* BIO */}
        {profile.bio && (
          <div style={{backgroundColor:'#1a1d24', border:'1px solid #2a2d35', borderRadius:'16px',
            padding:'16px', marginBottom:'16px'}}>
            <p style={{color:'#d1d5db', fontSize:'13px', lineHeight:'1.7'}}>{profile.bio}</p>
          </div>
        )}

        {/* STATS */}
        <div style={{display:'grid', gridTemplateColumns:'1fr 1fr', gap:'10px', marginBottom:'16px'}}>
          <div style={{backgroundColor:'#1a1d24', border:'1px solid #2a2d35', borderRadius:'16px',
            padding:'16px', textAlign:'center'}}>
            <p style={{fontSize:'24px', marginBottom:'4px'}}>🤝</p>
            <p style={{color:'white', fontWeight:'800', fontSize:'22px'}}>{profile.totalEnvios}</p>
            <p style={{color:'#6b7280', fontSize:'11px'}}>Ayudas completadas</p>
          </div>
          <div style={{backgroundColor:'#1a1d24', border:'1px solid #2a2d35', borderRadius:'16px',
            padding:'16px', textAlign:'center'}}>
            <FaStar style={{color:'#f59e0b', fontSize:'22px', margin:'0 auto 4px', display:'block'}} />
            <p style={{color:'white', fontWeight:'800', fontSize:'22px'}}>{profile.stats.global || '—'}</p>
            <p style={{color:'#6b7280', fontSize:'11px'}}>Valoración media</p>
          </div>
        </div>

        {/* STATS POR ROL */}
        {profile.stats.total > 0 && (
          <div style={{display:'grid', gridTemplateColumns:'1fr 1fr 1fr', gap:'8px', marginBottom:'16px'}}>
            {['DRIVER','SENDER','RECEIVER'].map(rol => {
              const media = profile.stats.porRol[rol];
              if (!media) return null;
              const { label, icon, color, bg } = ROL_LABELS[rol];
              return (
                <div key={rol} style={{backgroundColor:'#1a1d24', border:'1px solid #2a2d35',
                  borderRadius:'14px', padding:'12px', textAlign:'center'}}>
                  <span style={{color, fontSize:'11px', display:'flex', alignItems:'center',
                    justifyContent:'center', gap:'3px', marginBottom:'4px'}}>
                    {icon} {label}
                  </span>
                  <p style={{color:'white', fontWeight:'700', fontSize:'14px'}}>
                    {media}<FaStar style={{display:'inline', color:'#f59e0b', fontSize:'9px', marginLeft:'2px'}} />
                  </p>
                </div>
              );
            })}
          </div>
        )}

        {/* VERIFICACIONES — próximamente */}
        <div style={{backgroundColor:'#1a1d24', border:'1px solid #2a2d35', borderRadius:'16px',
          padding:'14px 16px', marginBottom:'16px'}}>
          <div style={{display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:'10px'}}>
            <div style={{display:'flex', alignItems:'center', gap:'6px'}}>
              <FaShieldAlt style={{color:'#3a3d45', fontSize:'12px'}} />
              <p style={{color:'#6b7280', fontSize:'11px', fontWeight:'600', textTransform:'uppercase', letterSpacing:'0.05em'}}>
                Verificaciones
              </p>
            </div>
            <span style={{backgroundColor:'#f9731615', border:'1px solid #f9731630',
              color:'#f97316', fontSize:'10px', fontWeight:'600', padding:'2px 8px', borderRadius:'20px'}}>
              Próximamente
            </span>
          </div>
          <div style={{display:'flex', gap:'8px'}}>
            {['Email', 'Teléfono', 'Identidad'].map(v => (
              <div key={v} style={{flex:1, backgroundColor:'#111318', border:'1px solid #2a2d35',
                borderRadius:'10px', padding:'8px', textAlign:'center'}}>
                <div style={{width:'20px', height:'20px', borderRadius:'50%', backgroundColor:'#2a2d35',
                  margin:'0 auto 4px', display:'flex', alignItems:'center', justifyContent:'center'}}>
                  <span style={{color:'#3a3d45', fontSize:'10px'}}>○</span>
                </div>
                <p style={{color:'#3a3d45', fontSize:'10px'}}>{v}</p>
              </div>
            ))}
          </div>
        </div>

        {/* BOTONES ACCIÓN */}
        {me?.id !== userId && (
          <div style={{display:'flex', flexDirection:'column', gap:'8px', marginBottom:'20px'}}>
            <button onClick={abrirFormulario}
              style={{width:'100%', backgroundColor: showForm ? '#2a2d35' : '#f97316',
                color: showForm ? '#9ca3af' : 'white', fontWeight:'700',
                padding:'14px', borderRadius:'14px', fontSize:'14px', border:'none', cursor:'pointer'}}>
              {showForm ? 'Cancelar' : '⭐ Dejar un agradecimiento'}
            </button>
            <button onClick={() => setShowReport(true)}
              style={{width:'100%', backgroundColor:'transparent', border:'1px solid #2a2d35',
                color:'#6b7280', fontWeight:'600', padding:'12px', borderRadius:'14px',
                fontSize:'13px', cursor:'pointer'}}>
              🚨 Reportar usuario
            </button>
          </div>
        )}

        {/* FORMULARIO AGRADECIMIENTO */}
        {showForm && (
          <div style={{backgroundColor:'#1a1d24', border:'1px solid #2a2d35', borderRadius:'16px',
            padding:'20px', marginBottom:'20px'}}>
            <p style={{color:'white', fontWeight:'700', fontSize:'15px', marginBottom:'16px'}}>
              Nuevo agradecimiento
            </p>
            {loadingInteracciones ? (
              <p style={{color:'#6b7280', fontSize:'13px', textAlign:'center', padding:'16px 0'}}>
                Buscando vuestra historia en común...
              </p>
            ) : interaccionesDisponibles.length === 0 ? (
              <div style={{textAlign:'center', padding:'16px 0'}}>
                <p style={{color:'#9ca3af', fontSize:'13px', marginBottom:'4px'}}>
                  Solo puedes agradecer tras completar un envío juntos.
                </p>
                <p style={{color:'#6b7280', fontSize:'11px'}}>Cuando os ayudéis, podrás agradecérselo aquí 🤝</p>
              </div>
            ) : (
              <>
                <p style={{color:'#6b7280', fontSize:'11px', marginBottom:'8px'}}>¿Por qué envío?</p>
                <div style={{display:'flex', flexDirection:'column', gap:'8px', marginBottom:'16px'}}>
                  {interaccionesDisponibles.map(i => {
                    const rol = ROL_LABELS[i.rolTarget];
                    const sel = interaccionSeleccionada?.packageId === i.packageId;
                    return (
                      <button key={i.packageId} onClick={() => setInteraccionSeleccionada(i)}
                        style={{border:`1px solid ${sel ? '#f97316' : '#2a2d35'}`,
                          backgroundColor: sel ? '#f9731610' : '#111318',
                          borderRadius:'12px', padding:'12px', textAlign:'left', width:'100%', cursor:'pointer'}}>
                        <div style={{display:'flex', alignItems:'center', justifyContent:'space-between'}}>
                          <div>
                            <p style={{color:'white', fontSize:'13px', fontWeight:'600'}}>{i.from} → {i.to}</p>
                            <span style={{color: rol?.color, fontSize:'11px', display:'flex', alignItems:'center', gap:'4px', marginTop:'2px'}}>
                              {rol?.icon} Como {rol?.label}
                            </span>
                          </div>
                          {sel && <FaCheckCircle style={{color:'#f97316'}} />}
                        </div>
                      </button>
                    );
                  })}
                </div>
                <p style={{color:'#6b7280', fontSize:'11px', marginBottom:'8px'}}>Puntuación</p>
                <div style={{display:'flex', gap:'12px', marginBottom:'16px'}}>
                  {[1,2,3,4,5].map(n => (
                    <button key={n} onClick={() => setFormData(f => ({...f, score: n}))} style={{background:'none', border:'none', cursor:'pointer', padding:0}}>
                      <FaStar size={28} style={{color: n <= formData.score ? '#f59e0b' : '#374151'}} />
                    </button>
                  ))}
                </div>
                <textarea rows={3} placeholder="¿Cómo fue la experiencia? (opcional)"
                  value={formData.comment}
                  onChange={e => setFormData(f => ({...f, comment: e.target.value}))}
                  style={{width:'100%', backgroundColor:'#111318', border:'1px solid #2a2d35',
                    borderRadius:'12px', padding:'11px 14px', color:'white', fontSize:'13px',
                    resize:'none', lineHeight:'1.5', marginBottom:'8px', outline:'none'}}
                  className="placeholder-gray-600" />
                <p style={{color:'#3a3d45', fontSize:'11px', marginBottom:'14px'}}>
                  Los agradecimientos no pueden eliminarse.
                </p>
                {formMsg && (
                  <p style={{fontSize:'13px', marginBottom:'10px',
                    color: formMsg.includes('!') ? '#4ade80' : '#f87171'}}>
                    {formMsg}
                  </p>
                )}
                <button onClick={handleSubmit} disabled={sending || !interaccionSeleccionada}
                  style={{width:'100%', backgroundColor:'#f97316', color:'white', fontWeight:'700',
                    padding:'13px', borderRadius:'12px', fontSize:'14px', border:'none', cursor:'pointer',
                    opacity: (sending || !interaccionSeleccionada) ? 0.5 : 1}}>
                  {sending ? 'Enviando...' : 'Enviar agradecimiento 🤝'}
                </button>
              </>
            )}
          </div>
        )}

        {/* AGRADECIMIENTOS */}
        <div>
          <div style={{display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:'14px'}}>
            <p style={{color:'white', fontWeight:'700', fontSize:'15px'}}>
              Agradecimientos <span style={{color:'#6b7280', fontWeight:'400'}}>({profile.stats.total})</span>
            </p>
          </div>
          <div style={{display:'flex', gap:'6px', marginBottom:'14px', overflowX:'auto', paddingBottom:'4px'}}>
            {['TODOS', 'DRIVER', 'SENDER', 'RECEIVER'].map(f => (
              <button key={f} onClick={() => setFiltroRol(f)}
                style={filtroRol === f
                  ? {backgroundColor:'#f97316', border:'1px solid #f97316', color:'white',
                     fontSize:'11px', fontWeight:'700', padding:'5px 14px', borderRadius:'20px', whiteSpace:'nowrap', cursor:'pointer'}
                  : {backgroundColor:'#1a1d24', border:'1px solid #2a2d35', color:'#6b7280',
                     fontSize:'11px', padding:'5px 14px', borderRadius:'20px', whiteSpace:'nowrap', cursor:'pointer'}
                }>
                {f === 'TODOS' ? 'Todos' : ROL_LABELS[f].label}
              </button>
            ))}
          </div>

          {ratingsFiltradas.length === 0 ? (
            <div style={{textAlign:'center', padding:'40px 0'}}>
              <div style={{fontSize:'32px', opacity:0.2, marginBottom:'8px'}}>🤝</div>
              <p style={{color:'#6b7280', fontSize:'13px'}}>
                {filtroRol === 'TODOS' ? 'Aún no tiene agradecimientos' : `Sin agradecimientos como ${ROL_LABELS[filtroRol]?.label}`}
              </p>
            </div>
          ) : (
            ratingsFiltradas.map(r => (
              <RatingCard key={r.id} rating={r} onViewProfile={setViewingProfile} />
            ))
          )}
        </div>
      </div>
{showFoto && profile.avatar && (
  <div onClick={() => setShowFoto(false)}
    style={{position:'fixed', top:0, left:0, width:'100%', height:'100%',
      backgroundColor:'rgba(0,0,0,0.9)', zIndex:9999,
      display:'flex', alignItems:'center', justifyContent:'center', cursor:'pointer'}}>
    <img src={profile.avatar}
      style={{width:'280px', height:'280px', borderRadius:'50%', objectFit:'cover',
        border:'4px solid #f97316', boxShadow:'0 0 60px rgba(249,115,22,0.3)'}} />
  </div>
)}
      {showReport && (
        <ReportModal userId={userId} userName={profile.name} onClose={() => setShowReport(false)} />
      )}
    </div>
  );
}